import React, { useEffect, useState } from 'react';
import { Activity, ChevronLeft, ChevronRight, Search, Filter } from 'lucide-react';
import { useApi } from './DashboardLayout';

const ACTION_LABELS = {
  login: 'Anmeldung',
  impersonate: 'Impersonation',
  twofa_toggle: '2FA geändert',
  create: 'Erstellt',
  update: 'Aktualisiert',
  delete: 'Gelöscht',
  archive: 'Archiviert',
  restore: 'Wiederhergestellt',
  send_mail: 'E-Mail gesendet',
};

const ACTION_COLORS = {
  login: '#10B981',
  impersonate: '#F59E0B',
  twofa_toggle: '#8B5CF6',
  create: '#3B82F6',
  update: '#6366F1',
  delete: '#EF4444',
  archive: '#6B7280',
  restore: '#10B981',
  send_mail: '#0EA5E9',
};

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const { apiFetch } = useApi();
  const perPage = 25;

  useEffect(() => { load(); }, [page, actionFilter]);

  const load = async () => {
    setLoading(true);
    let url = `/api/audit-log?limit=${perPage}&skip=${page * perPage}`;
    if (actionFilter) url += `&action=${actionFilter}`;
    if (search) url += `&user_email=${encodeURIComponent(search)}`;
    const r = await apiFetch(url);
    const d = await r?.json();
    if (d) { setLogs(d.logs || []); setTotal(d.total || 0); }
    setLoading(false);
  };

  const doSearch = () => { setPage(0); load(); };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div data-testid="audit-log-page">
      <div className="page-header">
        <h2>Aktivitätsprotokoll</h2>
        <p>{total} Einträge insgesamt</p>
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-body" style={{ padding: '10px 16px', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 200 }}>
            <Search size={16} style={{ color: 'var(--text-secondary)' }} />
            <input className="form-input" placeholder="Benutzer-E-Mail suchen..." value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && doSearch()}
              data-testid="audit-search"
              style={{ border: 'none', padding: '6px 0', boxShadow: 'none' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Filter size={14} style={{ color: 'var(--text-secondary)' }} />
            <select className="form-input" value={actionFilter} onChange={e => { setActionFilter(e.target.value); setPage(0); }}
              data-testid="audit-filter-action"
              style={{ width: 'auto', height: 36, padding: '4px 10px', fontSize: 13 }}>
              <option value="">Alle Aktionen</option>
              {Object.entries(ACTION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={doSearch} data-testid="audit-search-btn">Suchen</button>
        </div>
      </div>

      <div className="card">
        {loading ? <div className="empty-state"><p>Laden...</p></div> : (
          <div className="table-wrap">
            <table data-testid="audit-table">
              <thead>
                <tr>
                  <th>Zeitpunkt</th>
                  <th>Benutzer</th>
                  <th>Aktion</th>
                  <th>Entität</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l, i) => (
                  <tr key={i} data-testid={`audit-row-${i}`}>
                    <td style={{ whiteSpace: 'nowrap', fontSize: 13, color: 'var(--text-secondary)' }}>
                      {l.timestamp ? new Date(l.timestamp).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                    </td>
                    <td style={{ fontWeight: 500 }}>{l.user_email || '-'}</td>
                    <td>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                        background: `${ACTION_COLORS[l.action] || '#6B7280'}15`,
                        color: ACTION_COLORS[l.action] || '#6B7280'
                      }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: ACTION_COLORS[l.action] || '#6B7280' }} />
                        {ACTION_LABELS[l.action] || l.action}
                      </span>
                    </td>
                    <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                      {l.entity_type}{l.entity_id ? ` #${l.entity_id}` : ''}
                    </td>
                    <td style={{ fontSize: 13, color: 'var(--text-secondary)', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {l.details || '-'}
                    </td>
                  </tr>
                ))}
                {logs.length === 0 && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                    Keine Einträge gefunden.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: '1rem' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setPage(p => p - 1)} disabled={page === 0} data-testid="audit-prev">
            <ChevronLeft size={14} /> Zurück
          </button>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>
            Seite {page + 1} von {totalPages}
          </span>
          <button className="btn btn-secondary btn-sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1} data-testid="audit-next">
            Weiter <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
